import * as React from "react";

import Translator from "app/components/common/Translator";


// A bar placed in the middle of [from, to] on the x-axis with the given height.
export interface BarChartEntry {
    from: number;
    to: number;
    value: number;
    tooltip?: string[];
}

// Horizontal line across the plot, e.g. an average or median.
export interface BarChartReferenceLine {
    value: number;
    label: string;
}

interface Props {
    data: BarChartEntry[];
    xLabel: string;
    yLabel: string;
    referenceLine?: BarChartReferenceLine;
}

interface State {
    width: number;
    hovered?: number;
}

const height = 260;
const margin = { top: 24, right: 16, bottom: 44, left: 56 };
const gap = 2;
const maxBarWidth = 24;
// bars higher than this multiple of the average are cut off so the others stay visible
const outlierFactor = 2;
const radius = 4;

// Rounds the axis maximum up and returns evenly spaced, clean tick values.
function niceTicks(min: number, max: number, count: number) {
    const span = max - min || 1;
    const rawStep = span / count;
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const step = [1, 2, 2.5, 5, 10].map(f => f * magnitude).find(s => s >= rawStep)!;
    const start = Math.floor(min / step) * step;
    const end = Math.ceil(max / step) * step;
    let ticks: number[] = [];
    for (let t = start; t <= end + step / 2; t += step) {
        ticks.push(Math.round(t * 1e6) / 1e6);
    }
    return ticks;
}

// Two decimals, but keeps small values like 0.004 from showing as 0.
export function format(value: number) {
    return Math.abs(value) < 1 && value !== 0
        ? value.toLocaleString(undefined, { maximumSignificantDigits: 2 })
        : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

// Bar path with rounded top corners and a square base.
function barPath(x: number, y: number, w: number, h: number) {
    const r = Math.min(radius, w / 2, h);
    return `M${x},${y + h} V${y + r} Q${x},${y} ${x + r},${y} H${x + w - r} Q${x + w},${y} ${x + w},${y + r} V${y + h} Z`;
}

export class BarChart extends React.Component<Props, State> {
    public readonly state: State = { width: 600 }
    private container = React.createRef<HTMLDivElement>();
    private resize = () => {
        if (this.container.current) {
            this.setState({ width: this.container.current.clientWidth });
        }
    }

    componentDidMount() {
        this.resize();
        window.addEventListener("resize", this.resize);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.resize);
    }

    render() {
        const data = this.props.data;
        if (data.length === 0) {
            return (
                <div className="c-chart" ref={this.container}>
                    <div className="c-table__empty"><Translator text="Not enough data" /></div>
                </div>
            );
        }

        const xMin = Math.min(...data.map(d => d.from));
        const xMax = Math.max(...data.map(d => d.to));
        const plotWidth = Math.max(this.state.width - margin.left - margin.right, 1);
        // as many x labels as fit side by side; ~7px per character at the 12px tick font
        const xLabelWidth = format(xMax).length * 7 + 16;
        const xTicks = niceTicks(xMin, xMax, Math.max(Math.floor(plotWidth / xLabelWidth), 1)).filter(t => t >= xMin && t <= xMax);
        const values = data.map(d => d.value);
        const average = values.reduce((sum, v) => sum + v, 0) / values.length;
        const max = Math.max(...values);
        const yTicks = niceTicks(0, max > average * outlierFactor ? average * outlierFactor : max, 4);
        const yMax = yTicks[yTicks.length - 1] || 1;
        const plotHeight = height - margin.top - margin.bottom;
        // inset the x-range so bars at the edges stay inside the plot
        const x = (v: number) => margin.left + maxBarWidth / 2 + (v - xMin) / ((xMax - xMin) || 1) * (plotWidth - maxBarWidth);
        const middle = (d: BarChartEntry) => x((d.from + d.to) / 2);
        // all bars share one width, narrowed when neighbours are closer than the maximum width
        const middles = data.map(middle).sort((a, b) => a - b);
        const barWidth = Math.max(Math.min(maxBarWidth, ...middles.slice(1).map((m, i) => m - middles[i] - gap)), 2);
        const y = (v: number) => margin.top + plotHeight - v / yMax * plotHeight;
        const hovered = this.state.hovered !== undefined ? data[this.state.hovered] : undefined;

        return (
            <div className="c-chart" ref={this.container}>
                <svg className="c-chart__svg" width={this.state.width} height={height}>
                    {yTicks.map(t => (
                        <g key={"y" + t}>
                            <line className={t === 0 ? "c-chart__axis" : "c-chart__grid"} x1={margin.left} x2={margin.left + plotWidth} y1={y(t)} y2={y(t)} />
                            <text className="c-chart__tick" x={margin.left - 8} y={y(t)} textAnchor="end" dominantBaseline="middle">{format(t)}</text>
                        </g>
                    ))}
                    {xTicks.map(t => (
                        <text key={"x" + t} className="c-chart__tick" x={x(t)} y={margin.top + plotHeight + 18} textAnchor="middle">{format(t)}</text>
                    ))}
                    <text className="c-chart__axis-label" x={margin.left + plotWidth / 2} y={height - 6} textAnchor="middle">{this.props.xLabel}</text>
                    <text className="c-chart__axis-label" transform={`translate(14, ${margin.top + plotHeight / 2}) rotate(-90)`} textAnchor="middle">{this.props.yLabel}</text>
                    {/* drawn before the bars so it stays in the background */}
                    {this.props.referenceLine &&
                        <line className="c-chart__reference-line" x1={margin.left} x2={margin.left + plotWidth} y1={y(this.props.referenceLine.value)} y2={y(this.props.referenceLine.value)} />
                    }
                    {data.map((d, index) => {
                        const x0 = middle(d) - barWidth / 2;
                        const cut = d.value > yMax;
                        const top = y(Math.min(d.value, yMax));
                        return (
                            <g key={index} onMouseEnter={() => this.setState({ hovered: index })} onMouseLeave={() => this.setState({ hovered: undefined })}>
                                <rect className="c-chart__hit" x={Math.min(x(d.from), x0)} y={margin.top} width={Math.max(x(d.to) - x(d.from), barWidth)} height={plotHeight} />
                                <path className={this.state.hovered === index ? "c-chart__bar c-chart__bar--hovered" : "c-chart__bar"} d={barPath(x0, top, barWidth, margin.top + plotHeight - top)} />
                                {cut &&
                                    <>
                                        <line className="c-chart__break" x1={x0 - 2} x2={x0 + barWidth + 2} y1={top + 10} y2={top + 6} />
                                        <text className="c-chart__tick" x={middle(d)} y={margin.top - 8} textAnchor="middle">{format(d.value)}</text>
                                    </>
                                }
                            </g>
                        );
                    })}
                    {this.props.referenceLine &&
                        <text className="c-chart__reference-label" x={margin.left + plotWidth} y={y(this.props.referenceLine.value) - 6} textAnchor="end">
                            {this.props.referenceLine.label + ": " + format(this.props.referenceLine.value)}
                        </text>
                    }
                </svg>
                {hovered && hovered.tooltip &&
                    <div className={y(Math.min(hovered.value, yMax)) < margin.top + plotHeight / 2 ? "c-chart__tooltip c-chart__tooltip--below" : "c-chart__tooltip"} style={{ left: Math.min(Math.max(middle(hovered), 80), this.state.width - 80), top: y(Math.min(hovered.value, yMax)) + 12 }}>
                        {hovered.tooltip.map(t => <div key={t}>{t}</div>)}
                    </div>
                }
            </div>
        );
    }
}
